import React, { useEffect, useRef, useState, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";

const GREEN = "#237A35";
const THRESHOLD = 70;   // px pulled (after resistance) to trigger a refresh
const MAX = 120;        // max visual pull
const WHEEL_RESET_MS = 260;

const spin = keyframes`to { transform: rotate(360deg); }`;

const Bar = styled.div`
  position: fixed; top: 0; left: 0; right: 0;
  display: flex; justify-content: center;
  pointer-events: none; z-index: 100000;
`;
const Puck = styled.div`
  width: 36px; height: 36px; border-radius: 50%;
  background: #fff; box-shadow: 0 3px 12px rgba(0,0,0,0.20);
  display: flex; align-items: center; justify-content: center;
`;
const Spinner = styled.svg`
  width: 20px; height: 20px;
  ${({ $spin }) => $spin && css`animation: ${spin} 0.8s linear infinite;`}
`;

/**
 * Pull-to-refresh for the device detail pages. Works with:
 *   - touch swipe down from the top (phones/tablets)
 *   - trackpad / mouse-wheel over-scroll up while at the top (desktop)
 * Drag past the threshold to reload. Attach once per page.
 */
export default function PullToRefresh({ onRefresh, zone = "[data-ptr-header]" }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(null);
  const pullRef = useRef(0);
  const activeRef = useRef(false);   // dragging → disable transition
  const wheelAcc = useRef(0);
  const wheelTimer = useRef(null);

  const doRefresh = useCallback(() => {
    setRefreshing(true);
    activeRef.current = false;
    pullRef.current = THRESHOLD;
    setPull(THRESHOLD);
    if (onRefresh) onRefresh();
    else setTimeout(() => window.location.reload(), 200);
  }, [onRefresh]);

  useEffect(() => {
    const atTop = () =>
      Math.min(
        window.scrollY || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      ) <= 0;

    const setP = (v) => { pullRef.current = v; setPull(v); };

    // Only arm the pull when the gesture starts inside the page header zone.
    const inZone = (t) => !!(t && t.closest && t.closest(zone));

    /* ── Touch ── */
    const onTouchStart = (e) => {
      if (refreshing || !atTop()) return;
      const t = e.target;
      if (!inZone(t)) return;
      if (t.closest("input, textarea, button, select, a")) return;
      touchStartY.current = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (touchStartY.current == null) return;
      const dy = e.touches[0].clientY - touchStartY.current;
      if (dy <= 0 || !atTop()) { setP(0); return; }
      activeRef.current = true;
      setP(Math.min(dy * 0.5, MAX));
      if (e.cancelable) e.preventDefault();   // stop native rubber-band
    };
    const onTouchEnd = () => {
      if (touchStartY.current == null) return;
      activeRef.current = false;
      if (pullRef.current >= THRESHOLD) doRefresh();
      else setP(0);
      touchStartY.current = null;
    };

    /* ── Wheel / trackpad over-scroll ── */
    const onWheel = (e) => {
      if (refreshing) return;
      if (!inZone(e.target)) { wheelAcc.current = 0; if (pullRef.current) setP(0); return; }
      if (!atTop()) { wheelAcc.current = 0; if (pullRef.current) setP(0); return; }
      if (e.deltaY < 0) {                     // scrolling up while at top = pulling down
        wheelAcc.current += -e.deltaY;
        activeRef.current = true;
        const resisted = Math.min(wheelAcc.current * 0.6, MAX);
        setP(resisted);
        if (e.cancelable) e.preventDefault();
        if (resisted >= THRESHOLD) { doRefresh(); return; }
        clearTimeout(wheelTimer.current);
        wheelTimer.current = setTimeout(() => {
          activeRef.current = false; wheelAcc.current = 0; setP(0);
        }, WHEEL_RESET_MS);
      } else {
        wheelAcc.current = 0;
        if (pullRef.current) setP(0);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("wheel", onWheel);
      clearTimeout(wheelTimer.current);
    };
  }, [doRefresh, refreshing, zone]);

  if (pull <= 0 && !refreshing) return null;

  const progress = Math.min(pull / THRESHOLD, 1);
  const y = 10 + progress * 34;               // slide the puck down as you pull
  const rot = refreshing ? 0 : progress * 300;

  return (
    <Bar
      style={{
        transform: `translateY(${y}px)`,
        opacity: refreshing ? 1 : Math.max(progress, 0.25),
        transition: activeRef.current ? "none" : "transform .2s ease, opacity .2s ease",
      }}
    >
      <Puck>
        <Spinner
          $spin={refreshing}
          viewBox="0 0 24 24"
          fill="none"
          style={{ transform: refreshing ? undefined : `rotate(${rot}deg)` }}
        >
          <circle cx="12" cy="12" r="9" stroke="#E5E7EB" strokeWidth="3" />
          <path d="M12 3 a9 9 0 0 1 9 9" stroke={GREEN} strokeWidth="3" strokeLinecap="round" />
        </Spinner>
      </Puck>
    </Bar>
  );
}